import { Eye, EyeOff, MarkerPin01 } from "@untitledui/icons";
import type { ReactNode } from "react";
import type {
	AddressDisplayProps,
	SimpleAddressDisplayProps,
	ToggleContentOptions,
} from "../../types/cards.types";

const loadingText = "Carregando...";
const emptyText = "Clique para exibir localização";

const getTextClassName = (variant?: string) => {
	if (variant === "vehicle" || variant === "toggle") {
		return "min-w-0 flex-1 truncate text-[14px] text-gray-500";
	}
	return "flex-1 truncate text-[14px] text-gray-500";
};

const getIconClassName = (variant?: string) => {
	if (variant === "vehicle") {
		return "h-4 w-4 text-[#000000]";
	}
	return "shrink-0 text-gray-500";
};

const renderSimpleContent = (
	address: string | null,
	isLoading: boolean,
	onClick?: () => void,
	variant?: string
): ReactNode => {
	const textClassName = getTextClassName(variant);

	if (isLoading) {
		return <span className={textClassName}>{loadingText}</span>;
	}

	if (address) {
		return <span className={textClassName}>{address}</span>;
	}

	if (onClick) {
		return (
			<button
				className={textClassName}
				onClick={(e) => {
					e.stopPropagation();
					onClick();
				}}
				type="button"
			>
				{emptyText}
			</button>
		);
	}

	return <span className={textClassName}>{emptyText}</span>;
};

const getToggleAddressText = (
	address: string | null,
	isLoading: boolean,
	showToggle: boolean
): string => {
	if (!showToggle) {
		return emptyText;
	}

	if (isLoading) {
		return loadingText;
	}

	return address ?? emptyText;
};

const renderToggleContent = ({
	address,
	isLoading,
	showToggle,
	onToggle,
	toggleLabel,
}: ToggleContentOptions): ReactNode => {
	const shouldRenderToggleLabel = Boolean(toggleLabel);
	const toggleText = getToggleAddressText(address, isLoading, showToggle);

	return (
		<button
			className="flex cursor-pointer items-start gap-2 overflow-hidden text-left"
			onClick={(e) => {
				e.stopPropagation();
				onToggle();
			}}
			type="button"
		>
			<div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
				{shouldRenderToggleLabel ? (
					<span className="shrink-0 pt-0.5 font-semibold text-[#414651] text-[14px]">
						{toggleLabel}:
					</span>
				) : null}
				<span className="wrap-break-word min-w-0 flex-1 overflow-hidden text-[#414651] text-[14px]">
					{toggleText}
				</span>
			</div>
			{showToggle ? (
				<Eye className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
			) : (
				<EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-gray-300" />
			)}
		</button>
	);
};

export const AddressDisplay = (props: AddressDisplayProps) => {
	const { address, isLoading, className = "", showMarkerIcon = true } = props;
	const variant = props.variant;

	if (variant === "toggle") {
		const { showToggle = false, onToggle, label: toggleLabel } = props;
		const handleToggle = onToggle ?? (() => null);

		return (
			<div className={`flex min-w-0 items-center gap-2.5 ${className}`}>
				{showMarkerIcon ? (
					<MarkerPin01 className={getIconClassName(variant)} size={16} />
				) : null}
				{renderToggleContent({
					address,
					isLoading,
					showToggle,
					onToggle: handleToggle,
					toggleLabel,
				})}
			</div>
		);
	}

	const simpleProps = props as SimpleAddressDisplayProps;
	const { onClick, showLabel, label: simpleLabel } = simpleProps;
	const shouldRenderSimpleLabel = Boolean(showLabel && simpleLabel);

	return (
		<div className={`flex min-w-0 items-center gap-2.5 ${className}`}>
			{showMarkerIcon ? (
				<MarkerPin01 className={getIconClassName(variant)} size={16} />
			) : null}
			{shouldRenderSimpleLabel ? (
				<span className="shrink-0 text-left text-[14px] text-gray-500">
					{simpleLabel}
				</span>
			) : null}
			{renderSimpleContent(address, isLoading, onClick, variant)}
		</div>
	);
};
